import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../supabase";
import { TabsBar } from "../components/TabsBar";

// ---------------------------------------------------------------------------
// Types — mirror public.schedule_overrides from the initial schema
// ---------------------------------------------------------------------------

type ScheduleSet = "regular" | "ramadan" | "summer";

const SCHEDULE_SETS: ScheduleSet[] = ["regular", "ramadan", "summer"];

type Override = {
  id: number;
  date_from: string; // YYYY-MM-DD
  date_to: string; // YYYY-MM-DD
  schedule_set: ScheduleSet;
  banner_text: string | null;
  no_service: boolean;
};

type FormState = {
  date_from: string;
  date_to: string;
  schedule_set: ScheduleSet;
  banner_text: string;
  no_service: boolean;
};

const todayIso = (): string => {
  // Use local date, not UTC, so "today" matches what the admin sees on the wall clock.
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const emptyForm = (): FormState => ({
  date_from: todayIso(),
  date_to: todayIso(),
  schedule_set: "regular",
  banner_text: "",
  no_service: false,
});

const QUICK_PRESETS: ReadonlyArray<{
  label: string;
  schedule_set: ScheduleSet;
  banner_text: string;
  no_service: boolean;
}> = [
  {
    label: "🌙 Ramadan timings",
    schedule_set: "ramadan",
    banner_text: "Ramadan timings in effect",
    no_service: false,
  },
  {
    label: "🚫 No service today",
    schedule_set: "regular",
    banner_text: "No service today",
    no_service: true,
  },
];

// ---------------------------------------------------------------------------
// Data fetching
// ---------------------------------------------------------------------------

async function fetchOverrides(): Promise<Override[]> {
  // Only show overrides that haven't fully ended (date_to >= today)
  // so admins see the current + upcoming set, not ancient history.
  const { data, error } = await supabase
    .from("schedule_overrides")
    .select("id, date_from, date_to, schedule_set, banner_text, no_service")
    .gte("date_to", todayIso())
    .order("date_from", { ascending: true });

  if (error) throw error;
  return (data ?? []) as Override[];
}

function activeToday(overrides: Override[]): Override | null {
  const today = todayIso();
  // Most-recently-started wins when multiple overlap (e.g., a one-day banner
  // sitting on top of a multi-day Ramadan window).
  const matches = overrides.filter(
    (o) => o.date_from <= today && o.date_to >= today,
  );
  if (matches.length === 0) return null;
  return matches.reduce((acc, cur) =>
    cur.date_from > acc.date_from ? cur : acc,
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function OverridesPage() {
  const [overrides, setOverrides] = useState<Override[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [quickPosting, setQuickPosting] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const rows = await fetchOverrides();
      setOverrides(rows);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const todays = useMemo(() => activeToday(overrides), [overrides]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!form.date_from || !form.date_to) {
      setFormError("Both start and end date are required.");
      return;
    }
    if (form.date_to < form.date_from) {
      setFormError("End date must be on or after the start date.");
      return;
    }

    setSubmitting(true);
    const payload = {
      date_from: form.date_from,
      date_to: form.date_to,
      schedule_set: form.schedule_set,
      banner_text: form.banner_text.trim() || null,
      no_service: form.no_service,
    };
    const { error: insertError } = await supabase
      .from("schedule_overrides")
      .insert(payload);
    setSubmitting(false);

    if (insertError) {
      setFormError(insertError.message);
      return;
    }

    setForm(emptyForm());
    setShowForm(false);
    void refresh();
  };

  const onDelete = async (id: number) => {
    if (
      !window.confirm(
        "Delete this schedule override? Passengers will no longer see its banner.",
      )
    ) {
      return;
    }
    setDeletingId(id);
    const { error: delError } = await supabase
      .from("schedule_overrides")
      .delete()
      .eq("id", id);
    setDeletingId(null);
    if (delError) {
      setError(delError.message);
      return;
    }
    void refresh();
  };

  const postQuick = async (
    preset: (typeof QUICK_PRESETS)[number],
  ) => {
    setQuickPosting(true);
    setError(null);
    const today = todayIso();
    const { error: insErr } = await supabase
      .from("schedule_overrides")
      .insert({
        date_from: today,
        date_to: today,
        schedule_set: preset.schedule_set,
        banner_text: preset.banner_text,
        no_service: preset.no_service,
      });
    setQuickPosting(false);
    if (insErr) {
      setError(insErr.message);
      return;
    }
    void refresh();
  };

  return (
    <main style={styles.page}>
      <header style={styles.header}>
        <div>
          <h1 style={styles.h1}>Schedule overrides</h1>
          <p style={styles.muted}>
            Ramadan, holidays, ad-hoc service notices. Changes go live to
            passengers the moment you save.
          </p>
        </div>
        <div style={styles.headerRight}>
          <Link to="/dashboard" style={styles.linkButton}>
            ← Dashboard
          </Link>
        </div>
      </header>

      <TabsBar active="dashboard" />

      {error && (
        <div
          style={{ ...styles.alert, background: "#fdecea", color: "#a02622" }}
        >
          {error}
        </div>
      )}

      {/* --- What passengers see today ----------------------------------- */}
      <section style={styles.card}>
        <h2 style={styles.h2}>What passengers see today</h2>
        {loading ? (
          <p style={styles.muted}>Loading…</p>
        ) : todays ? (
          <div
            style={{
              ...styles.previewBanner,
              background: todays.no_service ? "#fdecea" : "#fff4e5",
              color: todays.no_service ? "#a02622" : "#a05a00",
              borderColor: todays.no_service ? "#e0a3a0" : "#f0c674",
            }}
            aria-live="polite"
          >
            <strong>
              {todays.no_service
                ? "⚠ NO SERVICE — bus service is suspended."
                : `📣 ${todays.schedule_set.toUpperCase()} schedule in effect`}
            </strong>
            {todays.banner_text && (
              <p style={styles.previewBannerText}>{todays.banner_text}</p>
            )}
            <p style={styles.previewMeta}>
              {todays.date_from} → {todays.date_to}
            </p>
          </div>
        ) : (
          <p style={styles.muted}>
            No override covers today. Passengers see the default regular
            timetable.
          </p>
        )}
      </section>

      {/* --- Quick activate ---------------------------------------------- */}
      <section style={styles.card}>
        <h2 style={styles.h2}>Quick activate</h2>
        <div style={styles.quickGrid}>
          {QUICK_PRESETS.map((p) => (
            <button
              key={p.label}
              type="button"
              disabled={quickPosting}
              onClick={() => void postQuick(p)}
              style={{
                ...styles.quickButton,
                background: p.no_service ? "#fdecea" : "#1a3a5c",
                color: p.no_service ? "#a02622" : "#fff",
                borderColor: p.no_service ? "#e0a3a0" : "#1a3a5c",
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
      </section>

      {/* --- Add override ------------------------------------------------- */}
      <section style={styles.card}>
        <div style={styles.cardHeader}>
          <h2 style={styles.h2}>Active & upcoming overrides</h2>
          <button
            type="button"
            onClick={() => setShowForm((s) => !s)}
            style={styles.button}
          >
            {showForm ? "Cancel" : "+ Add override"}
          </button>
        </div>

        {showForm && (
          <form onSubmit={onSubmit} style={styles.form}>
            <div style={styles.formRow}>
              <label style={styles.label}>
                Start date
                <input
                  type="date"
                  required
                  value={form.date_from}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, date_from: e.target.value }))
                  }
                  style={styles.input}
                />
              </label>
              <label style={styles.label}>
                End date
                <input
                  type="date"
                  required
                  value={form.date_to}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, date_to: e.target.value }))
                  }
                  style={styles.input}
                />
              </label>
              <label style={styles.label}>
                Schedule set
                <select
                  value={form.schedule_set}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      schedule_set: e.target.value as ScheduleSet,
                    }))
                  }
                  style={styles.input}
                >
                  {SCHEDULE_SETS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <label style={styles.label}>
              Banner text (shown to passengers)
              <input
                type="text"
                placeholder="e.g. Ramadan timings in effect"
                value={form.banner_text}
                onChange={(e) =>
                  setForm((f) => ({ ...f, banner_text: e.target.value }))
                }
                style={styles.input}
              />
            </label>

            <label style={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={form.no_service}
                onChange={(e) =>
                  setForm((f) => ({ ...f, no_service: e.target.checked }))
                }
              />
              <span>No service — show &quot;No service today&quot; banner</span>
            </label>

            {formError && <p style={styles.error}>{formError}</p>}

            <div style={styles.formActions}>
              <button
                type="submit"
                disabled={submitting}
                style={styles.button}
              >
                {submitting ? "Saving…" : "Save override"}
              </button>
            </div>
          </form>
        )}

        {loading ? (
          <p style={styles.muted}>Loading overrides…</p>
        ) : overrides.length === 0 ? (
          <p style={styles.muted}>
            No active or upcoming overrides. The regular schedule is in effect.
          </p>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Date range</th>
                <th style={styles.th}>Schedule set</th>
                <th style={styles.th}>Banner</th>
                <th style={styles.th}>No service</th>
                <th style={styles.th} aria-label="actions" />
              </tr>
            </thead>
            <tbody>
              {overrides.map((o) => (
                <tr key={o.id}>
                  <td style={styles.td}>
                    {o.date_from} → {o.date_to}
                  </td>
                  <td style={styles.td}>
                    <span style={styles.scheduleSetBadge}>{o.schedule_set}</span>
                  </td>
                  <td style={styles.td}>{o.banner_text ?? "—"}</td>
                  <td style={styles.td}>
                    {o.no_service ? (
                      <span
                        style={{
                          ...styles.badge,
                          background: "#c0392b",
                        }}
                      >
                        YES
                      </span>
                    ) : (
                      <span
                        style={{
                          ...styles.badge,
                          background: "#9aa0a6",
                        }}
                      >
                        no
                      </span>
                    )}
                  </td>
                  <td style={{ ...styles.td, textAlign: "right" }}>
                    <button
                      type="button"
                      onClick={() => void onDelete(o.id)}
                      disabled={deletingId === o.id}
                      style={styles.dangerButton}
                    >
                      {deletingId === o.id ? "Deleting…" : "Delete"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </main>
  );
}

// ---------------------------------------------------------------------------
// Styles — kept inline to match the rest of the admin app (zero extra deps)
// ---------------------------------------------------------------------------

const styles: Record<string, React.CSSProperties> = {
  page: {
    fontFamily: "system-ui, -apple-system, sans-serif",
    maxWidth: 960,
    margin: "0 auto",
    padding: 24,
    color: "#1a1a1a",
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
    marginBottom: 16,
  },
  headerRight: { display: "flex", alignItems: "center", gap: 12 },
  h1: { margin: 0, fontSize: 20 },
  h2: { margin: 0, fontSize: 16 },
  muted: { color: "#666", fontSize: 14, margin: 0 },
  alert: {
    padding: "10px 14px",
    border: "1px solid #f0c674",
    borderRadius: 6,
    marginBottom: 16,
    fontWeight: 600,
  },
  card: {
    padding: 20,
    border: "1px solid #ddd",
    borderRadius: 8,
    background: "#fff",
    marginBottom: 20,
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  previewBanner: {
    padding: "12px 14px",
    border: "1px solid",
    borderRadius: 6,
    fontSize: 14,
  },
  previewBannerText: {
    margin: "6px 0 0 0",
    fontWeight: 400,
  },
  previewMeta: {
    margin: "6px 0 0 0",
    fontSize: 12,
    opacity: 0.8,
  },
  quickGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: 12,
  },
  quickButton: {
    padding: "14px 16px",
    border: "1px solid",
    borderRadius: 6,
    cursor: "pointer",
    fontSize: 15,
    fontWeight: 600,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    padding: 12,
    border: "1px solid #eee",
    borderRadius: 6,
    background: "#fafafa",
  },
  formRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: 12,
  },
  formActions: { display: "flex", justifyContent: "flex-end" },
  label: { display: "flex", flexDirection: "column", gap: 4, fontSize: 14 },
  input: {
    padding: "8px 10px",
    border: "1px solid #ccc",
    borderRadius: 4,
    fontSize: 14,
  },
  checkboxRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 14,
  },
  button: {
    padding: "8px 14px",
    background: "#1a3a5c",
    color: "#fff",
    border: "none",
    borderRadius: 4,
    cursor: "pointer",
    fontSize: 14,
  },
  linkButton: {
    padding: "6px 10px",
    background: "#f1f1f1",
    color: "#1a1a1a",
    border: "1px solid #ccc",
    borderRadius: 4,
    textDecoration: "none",
    fontSize: 13,
  },
  dangerButton: {
    padding: "4px 10px",
    background: "#fff",
    color: "#c0392b",
    border: "1px solid #c0392b",
    borderRadius: 4,
    cursor: "pointer",
    fontSize: 13,
  },
  error: { color: "#c0392b", fontSize: 13, margin: 0 },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 14 },
  th: {
    textAlign: "left",
    padding: "8px 10px",
    borderBottom: "2px solid #1a3a5c",
  },
  td: {
    padding: "8px 10px",
    borderBottom: "1px solid #eee",
    verticalAlign: "middle",
  },
  badge: {
    display: "inline-block",
    padding: "2px 8px",
    color: "#fff",
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: 0.5,
  },
  scheduleSetBadge: {
    display: "inline-block",
    padding: "2px 8px",
    background: "#e7eef6",
    color: "#1a3a5c",
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: 0.5,
    textTransform: "uppercase",
  },
};
